import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Loader2, Search, X, ClipboardCheck } from 'lucide-react';

/**
 * Typeahead for PO + PO Line Item pairs. Uses `GET /po-dropdown`.
 * Planners select an existing PO+Line combo instead of typing free-text.
 *
 * Props:
 *   projectId, value: {poNumber, poLineItem} | null
 *   onChange({poNumber, poLineItem, vendor, poValue}) — parent lifts state
 */
export default function POLineItemPicker({ projectId, value, onChange, disabled }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams(); if (q) params.append('q', q);
        const { data } = await api.get(
          `/v1/projects/${projectId}/mci/control-tower/po-dropdown?${params}&limit=20`);
        setItems(data.items || []);
      } catch (e) { /* ignore */ }
      setBusy(false);
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, projectId]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const display = value?.poNumber ? `${value.poNumber} · L${value.poLineItem || '—'}` : '';

  return (
    <div ref={ref} className="relative" data-testid="po-line-picker">
      <div className="flex items-center gap-1">
        <div className="relative flex-1">
          <ClipboardCheck className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--tone-navy-text)' }} />
          <input value={open ? q : display} onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} placeholder="Search PO # or line item…"
            disabled={disabled}
            className="w-full pl-7 pr-7 py-1.5 rounded border text-xs"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid="po-picker-input" />
          {value?.poNumber && !open && (
            <button onClick={() => onChange(null)} className="absolute right-2 top-1/2 -translate-y-1/2"
              data-testid="po-picker-clear">
              <X className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border shadow-lg"
          style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
          {busy ? (
            <div className="p-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
              <Loader2 className="w-4 h-4 mx-auto animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
              No POs match. Add one by leaving this blank and typing the PO # directly.
            </div>
          ) : items.map((it, i) => (
            <button key={i} onClick={() => {
                onChange({ poNumber: it.poNumber, poLineItem: it.poLineItem,
                            vendor: it.vendor, poValue: it.poValue });
                setOpen(false); setQ('');
              }}
              className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-black/5"
              style={{ borderTop: i > 0 ? '1px solid var(--border-color)' : 'none' }}
              data-testid={`po-picker-item-${it.poNumber}-${it.poLineItem || 'x'}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold" style={{ color: 'var(--tone-navy-text)' }}>
                  {it.poNumber} · L{it.poLineItem || '—'}
                </span>
                <span className="font-mono text-[10px]" style={{ color: '#F26B1F' }}>
                  ${Math.round((it.poValue || 0) / 1000)}k
                </span>
              </div>
              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {it.vendor} · {(it.materialDescription || '').slice(0, 60)}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
